import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { AdminRequestError, getUser, getUsers } from "./api";
import { ApiError } from "@/lib/apiClient";
import type { AdminUserDetail, ListSearch } from "./schema";
import { canAdminister } from "./access";
import {
  accountAccessOptions,
  type AccountAccess,
} from "@/features/auth/access";
export const adminDenialKey = (actorId: string) =>
  ["admin-access-denied", actorId] as const;
export const adminDenialOptions = (actorId: string) =>
  queryOptions({
    queryKey: adminDenialKey(actorId),
    queryFn: (): { status: number } | null => null,
    enabled: false,
    gcTime: 0,
  });
export async function recoverAdminFailure(
  cache: QueryClient,
  actorId: string,
  failure: unknown,
) {
  if (
    cache.getQueryData<{ user: { id: string } } | null>(["session"])?.user
      .id !== actorId
  )
    return;
  if (
    (failure instanceof ApiError || failure instanceof AdminRequestError) &&
    [401, 403].includes(failure.status ?? 0)
  ) {
    // Cache only the denial status; a write error can contain submitted secrets.
    cache.setQueryData(adminDenialKey(actorId), { status: failure.status });
    await clearAdminData(cache, actorId);
    void cache.invalidateQueries({ queryKey: ["account-access", actorId] });
  } else {
    // Reconcile the target without throwing away drafts on an unrelated account fetch failure.
    void cache.invalidateQueries({ queryKey: adminKey(actorId) });
  }
}
export const adminKey = (actorId: string) => ["admin-users", actorId] as const;
export function usersOptions(actorId: string, search: ListSearch) {
  return queryOptions({
    queryKey: [...adminKey(actorId), "list", search],
    queryFn: ({ signal }) => getUsers(search, signal),
    staleTime: 30_000,
    gcTime: 0,
    retry: false,
  });
}
export function userOptions(actorId: string, id: string) {
  return queryOptions({
    queryKey: [...adminKey(actorId), "detail", id],
    queryFn: ({ signal }) => getUser(id, signal),
    staleTime: 30_000,
    gcTime: 0,
    retry: false,
  });
}
export async function clearAdminData(cache: QueryClient, actorId: string) {
  const predicate = (query: { queryKey: readonly unknown[] }) =>
    [
      "admin-messages",
      "admin-users",
      "admin-settings",
      "admin-queues",
      "admin-jobs",
      "admin-email-templates",
      "admin-plans",
    ].includes(String(query.queryKey[0])) && query.queryKey[1] === actorId;
  await cache.cancelQueries({ predicate });
  cache.removeQueries({ predicate });
}
export async function refreshAdminData(
  cache: QueryClient,
  actorId: string,
  targetId?: string,
) {
  await cache.cancelQueries({ queryKey: adminKey(actorId) });
  const session = cache.getQueryData<{ user: { id: string } } | null>([
    "session",
  ]);
  const account = cache.getQueryData<AccountAccess>([
    "account-access",
    actorId,
  ]);
  if (session?.user.id !== actorId || !canAdminister(account, actorId)) {
    await clearAdminData(cache, actorId);
    return;
  }
  try {
    const current = await cache.fetchQuery(accountAccessOptions(actorId));
    if (
      cache.getQueryData<{ user: { id: string } } | null>(["session"])?.user
        .id !== actorId ||
      !canAdminister(current, actorId)
    ) {
      await clearAdminData(cache, actorId);
      return;
    }
  } catch {
    await clearAdminData(cache, actorId);
    return;
  }
  if (targetId === actorId) {
    await cache.invalidateQueries({ queryKey: ["session"] });
    await cache.invalidateQueries({ queryKey: ["current-profile", actorId] });
  }
  if (
    cache.getQueryData<{ user: { id: string } } | null>(["session"])?.user
      .id !== actorId ||
    cache.getQueryState(["session"])?.status === "error"
  ) {
    await clearAdminData(cache, actorId);
    return;
  }
  await cache.invalidateQueries({ queryKey: adminKey(actorId) });
}

export async function applyAdminUser(
  cache: QueryClient,
  actorId: string,
  saved: AdminUserDetail,
  action: string,
) {
  if (
    cache.getQueryData<{ user: { id: string } } | null>(["session"])?.user
      .id !== actorId
  )
    return false;
  if (
    saved.id === actorId &&
    ["changeEmail", "changePassword", "signOut"].includes(action)
  ) {
    const { finishSignOut } = await import("@/features/auth/session");
    const { clearPendingInvitation } =
      await import("@/features/invitations/pendingInvitation");
    const { clearOAuthQuery } = await import("@/features/oauth/request");
    await finishSignOut({ error: null }, cache);
    clearPendingInvitation();
    clearOAuthQuery();
    if (typeof window !== "undefined") window.location.assign("/auth/sign-in");
    return false;
  }
  await cache.cancelQueries({ queryKey: adminKey(actorId) });
  if (
    cache.getQueryData<{ user: { id: string } } | null>(["session"])?.user
      .id !== actorId ||
    !canAdminister(
      cache.getQueryData<AccountAccess>(["account-access", actorId]),
      actorId,
    )
  )
    return false;
  cache.setQueryData(userOptions(actorId, saved.id).queryKey, saved);
  if (saved.id === actorId && action === "editProfile") {
    const { applySavedProfile } = await import("@/features/profile/queries");
    await applySavedProfile(cache, actorId, saved.profile);
  }
  await cache.invalidateQueries({ queryKey: [...adminKey(actorId), "list"] });
  return true;
}
